import React from 'react';
import { UserIcon, ShieldCheckIcon, CreditCardIcon, BellIcon, ClipboardListIcon, BarChart3Icon, SparklesIcon, CheckIcon, FileTextIcon, ClipboardCheckIcon, CalendarDaysIcon, ContactIcon, MegaphoneIcon, DropletIcon, DatabaseIcon } from 'lucide-react';
import { useLanguage } from '../../LanguageContext';
import { DX_LOGOS, dxPlatform, dxFoundation, dxAtnaChecks, dxServices, type Bi, type FoundationKey, type ServiceKey } from '../../../data/digitalExperienceSolution';
const FOUNDATION_ICONS: Record<FoundationKey, typeof UserIcon> = {
  sso: UserIcon,
  consent: ShieldCheckIcon,
  card: CreditCardIcon,
  notify: BellIcon,
  requests: ClipboardListIcon,
  data: BarChart3Icon
};
const SERVICE_ICONS: Record<ServiceKey, typeof UserIcon> = {
  requests: FileTextIcon,
  permits: ClipboardCheckIcon,
  leisure: CalendarDaysIcon,
  payments: CreditCardIcon,
  licences: ContactIcon,
  comms: MegaphoneIcon,
  water: DropletIcon,
  systems: DatabaseIcon
};
function usePick() {
  const {
    lang
  } = useLanguage();
  return (b: Bi) => lang === 'FR' ? b.fr : b.en;
}
export function PlatformCard() {
  const pick = usePick();
  return <div className="relative z-10 mx-auto max-w-[620px] rounded-[26px] bg-white border border-[#E2E0FA] shadow-[0_22px_50px_-26px_rgba(79,70,229,0.55)] px-6 sm:px-8 py-4 flex flex-col sm:flex-row items-center gap-4 sm:gap-6" data-id="element-3380" data-mp="19my3ls0v1d0x">
      <div className="flex items-center gap-3 shrink-0" data-id="element-3381" data-mp="10p5oooz1w7e2">
        <img src={DX_LOGOS.bciti} alt="BCITI" className="h-12 w-auto object-contain" data-id="element-3382" data-mp="1xdv5ebyxytyg" />
        <span className="w-px h-8 bg-[#D4D6E0]" aria-hidden="true" data-id="element-3383" data-mp="1tbgnbu33jy88" />
        <img src={DX_LOGOS.atna} alt="ATNA" className="h-10 w-auto object-contain" data-id="element-3384" data-mp="1ta5jxvdkcshn" />
      </div>
      <p className="font-sans text-[16px] leading-snug text-[#121A3A] text-center sm:text-left" data-id="element-3385" data-mp="1oezysyrzjhum">
        <span className="block" data-id="element-3386" data-mp="1jfwslb09r5l2">{pick(dxPlatform.line1)}</span>
        <span className="block" data-id="element-3387" data-mp="cyjfuvh82j30">{pick(dxPlatform.line2)}</span>
      </p>
    </div>;
}
export function FoundationRow() {
  const pick = usePick();
  return <div className="grid grid-cols-1 xl:grid-cols-[74fr_26fr] gap-5 items-stretch" data-id="element-3388" data-mp="9fsyawalzqg5">
      <div className="rounded-[26px] bg-[#F6F6FD] border border-[#EEEDFB] p-3 -mt-2" data-id="element-3389" data-mp="9dztlmjk3gas">
        <ul className="h-full rounded-[20px] bg-white border border-[#EEF0F4] grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-y-6 py-6 xl:divide-x xl:divide-[#EEF0F4]" data-id="element-3390" data-mp="ect6bcr0r39w">
          {dxFoundation.map(f => {
          const Icon = FOUNDATION_ICONS[f.key];
          return <li key={f.key} className="flex flex-col items-center text-center px-3" data-id="element-3391" data-mp="3mt5rw7pwj8nk">
                <span className="w-11 h-11 rounded-full bg-[#F1F0FD] flex items-center justify-center" aria-hidden="true" data-id="element-3392" data-mp="3rm7d0xgpc4fl">
                  <Icon className="w-5 h-5 text-[#4F46E5]" strokeWidth={1.7} data-id="element-3393" data-mp="3drxxw1uf4tza" />
                </span>
                <p className="mt-3 font-sans text-[14px] font-semibold text-[#121A3A] leading-tight" data-id="element-3394" data-mp="3htjjcbl0qgl3">{pick(f.label)}</p>
                <p className="mt-1 font-sans text-[12.5px] leading-snug text-[#6B7180] max-w-[150px]" data-id="element-3395" data-mp="38uy16hjgl3ku">{pick(f.text)}</p>
              </li>;
        })}
        </ul>
      </div>

      <div className="rounded-[26px] bg-white border border-[#E2E0FA] shadow-[0_18px_44px_-28px_rgba(79,70,229,0.45)] px-6 py-5" data-id="element-3396" data-mp="2paz6skj1tmj8">
        <p className="flex items-center gap-2.5" data-id="element-3397" data-mp="2z7v7w56mb6dr">
          <SparklesIcon className="w-6 h-6 text-[#4F46E5]" strokeWidth={1.8} aria-hidden="true" data-id="element-3398" data-mp="2z8n3vh44n64k" />
          <img src={DX_LOGOS.atna} alt="ATNA" className="h-7 w-auto object-contain" data-id="element-3399" data-mp="2u6xqvvxcgidu" />
        </p>
        <ul className="mt-3 space-y-2" data-id="element-3400" data-mp="386772vljvcht">
          {dxAtnaChecks.map(c => <li key={c.fr} className="flex items-center gap-2.5" data-id="element-3401" data-mp="3385p6dzv1j7x">
              <span className="w-[18px] h-[18px] rounded-full bg-[#4F46E5] flex items-center justify-center shrink-0" aria-hidden="true" data-id="element-3402" data-mp="3hycx3zs8rjud">
                <CheckIcon className="w-3 h-3 text-white" strokeWidth={3} data-id="element-3403" data-mp="3cz8p3ch1zs5m" />
              </span>
              <span className="font-sans text-[14.5px] text-[#121A3A]" data-id="element-3404" data-mp="3qzazyeqy1lbd">{pick(c)}</span>
            </li>)}
        </ul>
      </div>
    </div>;
}
export function ServicesStrip() {
  const pick = usePick();
  return <div className="rounded-[26px] bg-white border border-[#EEF0F4] shadow-[0_18px_44px_-32px_rgba(18,26,58,0.35)] px-6 py-6 grid grid-cols-1 xl:grid-cols-[230px_1fr] gap-6 items-center" data-id="element-3405" data-mp="1k84z7dpe84yg">
      <div data-id="element-3406" data-mp="1u0u8q31dv8r5">
        <p className="font-sans text-[17px] font-bold uppercase tracking-wide text-[#4F46E5]" data-id="element-3407" data-mp="1u25ajxb9q6fm">{pick(dxServices.heading)}</p>
        <p className="font-sans text-[14.5px] leading-snug text-[#5B6478] mt-1.5 max-w-[220px]" data-id="element-3408" data-mp="11bmza90e4yr4">{pick(dxServices.sub)}</p>
      </div>
      <ul className="grid grid-cols-2 sm:grid-cols-4 xl:grid-cols-8 gap-y-6 xl:divide-x xl:divide-[#EEF0F4]" data-id="element-3409" data-mp="16bj42agwwu7q">
        {dxServices.items.map(s => {
        const Icon = SERVICE_ICONS[s.key];
        return <li key={s.key} className="flex flex-col items-center text-center px-2" data-id="element-3410" data-mp="1fcpnaray4j4o">
              <span className="w-11 h-11 rounded-full bg-[#F1F0FD] flex items-center justify-center" aria-hidden="true" data-id="element-3411" data-mp="2mny6po226mtf">
                <Icon className="w-5 h-5 text-[#4F46E5]" strokeWidth={1.7} data-id="element-3412" data-mp="2qoi5sx6700i7" />
              </span>
              <p className="mt-2.5 font-sans text-[14px] leading-tight text-[#121A3A]" data-id="element-3413" data-mp="f26hksvlcxf3">{pick(s.title)}</p>
              <p className="font-sans text-[14px] leading-tight text-[#121A3A]" data-id="element-3414" data-mp="b3h83bdoc00q">{pick(s.text)}</p>
            </li>;
      })}
      </ul>
    </div>;
}